import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useForm } from '@tanstack/react-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiV2, ApiV2Error, type V2NotificationDelivery } from '@/lib/api-v2-client'
import { CatSleeping } from '@/components/mascot'
import { useUpgradePrompt } from '@/components/upgrade-prompt'
import { useAuth } from '@/hooks/use-auth'
import { isFreeTier } from '@/lib/subscription-access'
import { createAlertChannelFormSchema, getFieldError } from '@/lib/forms'
import { toast } from 'sonner'
import { useTour } from '@/hooks/use-tour'
import type { DriveStep } from 'driver.js'

type AlertsSearch = {
  connected?: string
  error?: string
}

export const Route = createFileRoute('/_authenticated/dashboard/alerts/')({
  component: AlertsPage,
  validateSearch: (search: Record<string, unknown>): AlertsSearch => ({
    connected: search.connected as string | undefined,
    error: search.error as string | undefined,
  }),
})

// driver.js marks the highlighted element as a popup trigger (aria-haspopup,
// aria-expanded, aria-controls). Those attributes are invalid on plain
// containers, so remove them when a step highlights a group of controls.
const stripPopupAria: DriveStep['onHighlighted'] = (el) => {
  el?.removeAttribute('aria-haspopup')
  el?.removeAttribute('aria-expanded')
  el?.removeAttribute('aria-controls')
}

const alertsTourSteps: DriveStep[] = [
  {
    element: '#tour-connect-buttons',
    onHighlighted: stripPopupAria,
    popover: {
      title: 'Quick Connect',
      description: 'Connect Slack, Discord, or Telegram in one click. These integrations use OAuth — no webhook URLs needed.',
    },
  },
  {
    element: '#tour-add-channel',
    popover: {
      title: 'Add a Channel',
      description: 'Create an email, Slack, Discord, or Telegram alert channel manually. Email alerts may go to spam — check your junk folder.',
    },
  },
  {
    element: '#tour-channels-list',
    onHighlighted: stripPopupAria,
    popover: {
      title: 'Your Alert Channels',
      description: 'All your configured notification channels appear here. Each monitor can use one or more channels.',
    },
  },
  {
    element: '#tour-delivery-history',
    popover: {
      title: 'Delivery History',
      description: 'Track every alert notification sent — see delivery status, failures, and timing.',
    },
  },
]

function AlertsPage() {
  const { connected, error } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useTour({ name: 'alerts', steps: alertsTourSteps })

  useEffect(() => {
    if (connected) {
      toast.success(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`)
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
      navigate({ to: '/dashboard/alerts', search: {}, replace: true })
    } else if (error) {
      const messages: Record<string, string> = {
        invalid_state: 'Invalid OAuth state. Please try again.',
        expired: 'OAuth session expired. Please try again.',
        access_denied: 'Access was denied.',
        connection_failed: 'Connection failed. Please try again.',
      }
      toast.error(messages[error] || `Connection error: ${error}`)
      navigate({ to: '/dashboard/alerts', search: {}, replace: true })
    }
  }, [connected, error, navigate, queryClient])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alerts"
        description="Manage notification channels and view delivery history"
        menu={[
          { label: 'Escalation policies', asChild: <Link to="/dashboard/alerts/escalation">Escalation policies</Link> },
          { label: 'On-call schedules', asChild: <Link to="/dashboard/alerts/on-call">On-call schedules</Link> },
        ]}
        menuLabel="More alert settings"
      />

      <Tabs defaultValue="channels">
        <TabsList>
          <TabsTrigger value="channels">Alert Channels</TabsTrigger>
          <TabsTrigger id="tour-delivery-history" value="history">Delivery History</TabsTrigger>
        </TabsList>
        <TabsContent value="channels">
          <AlertChannelsTab />
        </TabsContent>
        <TabsContent value="history">
          <DeliveryHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Alert Channels Tab ──────────────────────────────────────────────

function AlertChannelsTab() {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // A new webhook channel's signing secret: shown once, never again.
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null)
  const { show: showUpgradePrompt } = useUpgradePrompt()
  const { organization } = useAuth()
  // SMS + phone-call alerts cost money to send, so they're paid-plan only
  // (mirrors the backend gate in alert_channels.rs).
  const isPaidPlan = (organization?.plan ?? 'free') !== 'free'
  const PAID_CHANNEL_TYPES = ['sms', 'voice']
  // The free plan: email plus one chat channel. Webhooks are Pro too (SMS and calls already are).
  const freeTier = isFreeTier(organization)
  const isProOnlyChannel = (type: string) =>
    !isPaidPlan && (PAID_CHANNEL_TYPES.includes(type) || (freeTier && type === 'webhook'))

  const { data: channels, isLoading } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiV2.listAlertChannels(),
  })

  const createMutation = useMutation({
    mutationFn: (data: { name: string; type: string; config: Record<string, string> }) =>
      apiV2.createAlertChannel(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
      toast.success('Alert channel created')
      setIsDialogOpen(false)
      form.reset()
      if (res?.secret) setWebhookSecret(res.secret)
    },
    onError: (error) => {
      if (error instanceof ApiV2Error && error.status === 402) {
        showUpgradePrompt(error.message)
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to create channel')
      }
    },
  })

  const [deleteChannelId, setDeleteChannelId] = useState<string | null>(null)
  const [deleteChannelName, setDeleteChannelName] = useState<string>('')
  const [renameChannelId, setRenameChannelId] = useState<string | null>(null)
  const [renameChannelName, setRenameChannelName] = useState<string>('')

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteAlertChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
      toast.success('Alert channel deleted')
      setDeleteChannelId(null)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete channel')
    },
  })

  // One test alert through one channel (the same delivery path as real alerts).
  const testMutation = useMutation({
    mutationFn: (id: string) => apiV2.testAlertChannel(id),
    onSuccess: (res) => {
      if (res.ok) toast.success('Test alert sent')
      else toast.error(`Test alert failed: ${res.error ?? 'unknown error'}`)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to send test alert')
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => apiV2.updateAlertChannel(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
      toast.success('Alert channel renamed')
      setRenameChannelId(null)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to rename channel')
    },
  })

  // Early-warning (degraded) opt-in lives in the channel's config object —
  // spread the existing config so other keys (webhook_url, email, …) survive.
  const toggleDegradedMutation = useMutation({
    mutationFn: ({ id, config, enabled }: { id: string; config: Record<string, unknown>; enabled: boolean }) =>
      apiV2.updateAlertChannel(id, { config: { ...config, notify_degraded: enabled } }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
      toast.success(vars.enabled ? 'Early-warning alerts enabled' : 'Early-warning alerts disabled')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update channel')
    },
  })

  type ChannelType = 'email' | 'slack' | 'discord' | 'telegram' | 'webhook' | 'sms' | 'voice'

  const form = useForm({
    defaultValues: {
      name: '',
      channel_type: 'email' as ChannelType,
      config_value: '',
      config_value_2: '',
    },
    validators: {
      onSubmit: createAlertChannelFormSchema,
    },
    onSubmit: async ({ value }) => {
      const configObj: Record<string, string> =
        value.channel_type === 'email'
          ? { email: value.config_value }
          : value.channel_type === 'telegram'
            ? { bot_token: value.config_value, chat_id: value.config_value_2 }
            : value.channel_type === 'sms' || value.channel_type === 'voice'
              ? { phone_number: value.config_value }
              : value.channel_type === 'webhook'
                ? { url: value.config_value }
                : { webhook_url: value.config_value }

      createMutation.mutate({
        name: value.name,
        type: value.channel_type,
        config: configObj,
      })
    },
  })

  return (
    <div className="space-y-4">
      <div id="tour-connect-buttons" role="group" aria-label="Connect a channel" className="flex justify-end gap-2">
        <ConnectSlackButton />
        <ConnectDiscordButton />
        <ConnectTelegramButton />
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open)
          if (!open) form.reset()
        }}>
          <DialogTrigger asChild>
            <Button id="tour-add-channel">Add Channel</Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
            >
              <DialogHeader>
                <DialogTitle>New Alert Channel</DialogTitle>
                <DialogDescription>
                  Add a new channel to receive incident alerts
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <form.Field name="name">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="My Alert Channel"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={createMutation.isPending}
                      />
                      {field.state.meta.errors?.[0] && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors[0])}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name="channel_type">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="type">Channel Type</Label>
                      <Select
                        value={field.state.value}
                        onValueChange={(v) => {
                          // Paid-only channels: free users get the upgrade
                          // prompt instead of selecting (backend also rejects).
                          if (isProOnlyChannel(v)) {
                            showUpgradePrompt(
                              v === 'webhook'
                                ? 'Webhook alerts are available on paid plans. Upgrade to send signed JSON to your own endpoint.'
                                : 'SMS and phone-call alerts are available on paid plans. Upgrade to wake your on-call with a text or a ringing call.',
                            )
                            return
                          }
                          field.handleChange(v as ChannelType)
                        }}
                        disabled={createMutation.isPending}
                      >
                        <SelectTrigger aria-label="Channel type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: 'email', label: 'Email' },
                            { value: 'slack', label: 'Slack' },
                            { value: 'discord', label: 'Discord' },
                            { value: 'telegram', label: 'Telegram' },
                            { value: 'webhook', label: isProOnlyChannel('webhook') ? 'Webhook (signed JSON) — Pro' : 'Webhook (signed JSON)' },
                            { value: 'sms', label: isPaidPlan ? 'SMS (text message)' : 'SMS (text message) — Pro' },
                            { value: 'voice', label: isPaidPlan ? 'Phone call' : 'Phone call — Pro' },
                          ].map((ch) => (
                            <SelectItem key={ch.value} value={ch.value}>
                              {ch.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {freeTier && (
                        <p className="text-sm text-muted-foreground">
                          The free plan has email and one chat channel (Slack, Discord or Telegram). Webhooks, SMS and phone calls are on Pro.
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Subscribe selector={(state) => state.values.channel_type}>
                  {(channelType) => {
                    const fieldConfig: Record<ChannelType, { label: string; type: string; placeholder: string }> = {
                      email: { label: 'Email Address', type: 'email', placeholder: 'alerts@example.com' },
                      slack: { label: 'Slack Webhook URL', type: 'url', placeholder: 'https://hooks.slack.com/...' },
                      discord: { label: 'Discord Webhook URL', type: 'url', placeholder: 'https://discord.com/api/webhooks/...' },
                      telegram: { label: 'Bot Token', type: 'text', placeholder: '123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11' },
                      webhook: { label: 'Endpoint URL (https)', type: 'url', placeholder: 'https://example.com/webhooks/uptrack' },
                      sms: { label: 'Phone Number', type: 'tel', placeholder: '+12025550123' },
                      voice: { label: 'Phone Number', type: 'tel', placeholder: '+12025550123' },
                    }
                    const cfg = fieldConfig[channelType]
                    return (
                      <>
                        <form.Field name="config_value">
                          {(field) => (
                            <div className="space-y-2">
                              <Label htmlFor="config">{cfg.label}</Label>
                              <Input
                                id="config"
                                type={cfg.type}
                                placeholder={cfg.placeholder}
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                onBlur={field.handleBlur}
                                disabled={createMutation.isPending}
                              />
                              {channelType === 'email' && (
                                <p className="text-sm text-muted-foreground">
                                  Alert emails may land in your spam or junk folder. Please check there and mark as "not spam".
                                </p>
                              )}
                              {(channelType === 'sms' || channelType === 'voice') && (
                                <p className="text-sm text-muted-foreground">
                                  Use E.164 format (with country code), e.g. +12025550123.{' '}
                                  {channelType === 'voice'
                                    ? 'Phone calls ring for critical outages — press 1 to acknowledge and pause escalation.'
                                    : 'Best for on-call alerts. Reply STOP to opt out at any time.'}
                                </p>
                              )}
                              {field.state.meta.errors?.[0] && (
                                <p className="text-sm text-destructive">
                                  {getFieldError(field.state.meta.errors[0])}
                                </p>
                              )}
                            </div>
                          )}
                        </form.Field>
                        {channelType === 'telegram' && (
                          <form.Field name="config_value_2">
                            {(field) => (
                              <div className="space-y-2">
                                <Label htmlFor="config2">Chat ID</Label>
                                <Input
                                  id="config2"
                                  placeholder="-1001234567890"
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  onBlur={field.handleBlur}
                                  disabled={createMutation.isPending}
                                />
                              </div>
                            )}
                          </form.Field>
                        )}
                      </>
                    )
                  }}
                </form.Subscribe>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card id="tour-channels-list">
        <CardHeader>
          <CardTitle>Your Alert Channels</CardTitle>
          <CardDescription>
            Manage notification channels for your monitors. Email alerts may land in spam — check your junk folder and mark as "not spam".
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : channels?.data && channels.data.length > 0 ? (
            <div className="space-y-4">
              {channels.data.map((channel) => (
                <div
                  key={channel.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="break-words font-medium">{channel.name}</span>
                      <Badge variant="secondary">{channel.type}</Badge>
                      {channel.is_active ? (
                        <StatusBadge kind="up" label="Active" />
                      ) : (
                        <StatusBadge kind="paused" label="Disabled" />
                      )}
                    </div>
                    <p className="break-all font-mono text-sm text-muted-foreground">
                      {channel.type === 'email'
                        ? channel.config.email
                        : channel.type === 'slack'
                        ? 'Slack webhook configured'
                        : channel.type === 'sms' || channel.type === 'voice'
                        ? String(channel.config.phone_number ?? '')
                        : channel.config.url}
                    </p>
                    <label className="flex cursor-pointer items-start gap-2 pt-1 text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                        checked={channel.config.notify_degraded === true}
                        disabled={toggleDegradedMutation.isPending}
                        onChange={(e) =>
                          toggleDegradedMutation.mutate({
                            id: channel.id,
                            config: channel.config,
                            enabled: e.target.checked,
                          })
                        }
                      />
                      Early-warning (degraded) alerts — notify before an incident opens
                    </label>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => testMutation.mutate(channel.id)}
                      disabled={testMutation.isPending}
                    >
                      Send test
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setRenameChannelId(channel.id); setRenameChannelName(channel.name ?? '') }}
                      disabled={renameMutation.isPending}
                    >
                      Rename
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setDeleteChannelId(channel.id); setDeleteChannelName(channel.name ?? '') }}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-muted-foreground">
              No alert channels configured. Add one to receive incident notifications.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Rename dialog */}
      <Dialog open={!!webhookSecret} onOpenChange={(open) => { if (!open) setWebhookSecret(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook signing secret</DialogTitle>
            <DialogDescription>
              Copy this secret now. You will not see it again. Use it to check the X-Uptrack-Signature header on each
              request.
            </DialogDescription>
          </DialogHeader>
          <code className="block break-all rounded-sm border border-border bg-muted px-3 py-2 font-mono text-sm">{webhookSecret}</code>
          <p className="text-sm text-body">
            How to verify a request:{' '}
            <Link to="/guides/webhook-payloads" className="text-primary hover:underline">
              webhook guide
            </Link>
            .
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                void navigator.clipboard?.writeText(webhookSecret ?? '')
                toast.success('Secret copied')
              }}
            >
              Copy secret
            </Button>
            <Button onClick={() => setWebhookSecret(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameChannelId} onOpenChange={(open) => { if (!open) setRenameChannelId(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename alert channel</DialogTitle>
            <DialogDescription>
              Change the display name shown in lists and monitor detail pages. This doesn't affect where alerts are sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-channel-input">Name</Label>
            <Input
              id="rename-channel-input"
              value={renameChannelName}
              onChange={(e) => setRenameChannelName(e.target.value)}
              placeholder="e.g. Slack - Team #alerts"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameChannelId(null)}>Cancel</Button>
            <Button
              onClick={() => renameChannelId && renameMutation.mutate({ id: renameChannelId, name: renameChannelName.trim() })}
              disabled={renameMutation.isPending || !renameChannelName.trim()}
            >
              {renameMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteChannelId} onOpenChange={(open) => { if (!open) setDeleteChannelId(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete alert channel?</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteChannelName}</strong>. Monitors using this channel will stop sending alerts to it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteChannelId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteChannelId && deleteMutation.mutate(deleteChannelId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Integration Connect Buttons ─────────────────────────────────────

function ConnectSlackButton() {
  const mutation = useMutation({
    mutationFn: () => apiV2.getSlackAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.auth_url
    },
    onError: () => {
      toast.error('Failed to start Slack connection. Check server configuration.')
    },
  })

  return (
    <Button
      variant="outline"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? 'Connecting...' : 'Connect Slack'}
    </Button>
  )
}

function ConnectDiscordButton() {
  const mutation = useMutation({
    mutationFn: () => apiV2.getDiscordAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.auth_url
    },
    onError: () => {
      toast.error('Failed to start Discord connection. Check server configuration.')
    },
  })

  return (
    <Button
      variant="outline"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? 'Connecting...' : 'Connect Discord'}
    </Button>
  )
}

function ConnectTelegramButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [state, setState] = useState<string | null>(null)
  const [code, setCode] = useState<string | null>(null)
  const [dmUrl, setDmUrl] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const initMutation = useMutation({
    mutationFn: () => apiV2.getTelegramAuthUrl(),
    onSuccess: (data) => {
      setState(data.state)
      setCode(data.code)
      setDmUrl(data.dm_url)
      setIsOpen(true)
    },
    onError: () => {
      toast.error('Failed to start Telegram connection. Check server configuration.')
    },
  })

  // Poll for connection completion while dialog is open (10 min timeout)
  useEffect(() => {
    if (!isOpen || !state || !code) return

    const startTime = Date.now()
    const MAX_POLL_MS = 10 * 60 * 1000

    const interval = setInterval(async () => {
      if (Date.now() - startTime > MAX_POLL_MS) {
        clearInterval(interval)
        setIsOpen(false)
        setState(null)
        setCode(null)
        setDmUrl(null)
        toast.error('Connection timed out. Please try again.')
        return
      }
      try {
        const result = await apiV2.getTelegramStatus(state, code)
        if (result.connected) {
          clearInterval(interval)
          setIsOpen(false)
          setState(null)
          setCode(null)
          setDmUrl(null)
          toast.success('Telegram connected successfully!')
          queryClient.invalidateQueries({ queryKey: ['alert-channels'] })
        }
      } catch {
        // ignore polling errors
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [isOpen, state, code, queryClient])

  return (
    <>
      <Button
        variant="outline"
        onClick={() => initMutation.mutate()}
        disabled={initMutation.isPending}
      >
        {initMutation.isPending ? 'Connecting...' : 'Connect Telegram'}
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) { setState(null); setCode(null); setDmUrl(null) }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Telegram</DialogTitle>
            <DialogDescription>
              Receive monitoring alerts via @UptrackAppBot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-3">
            <div className="space-y-3">
              <p className="text-base font-medium">Personal DM</p>
              <p className="text-sm text-muted-foreground">
                Click below and press <strong>Start</strong> in Telegram.
              </p>
              {dmUrl && (
                <Button asChild size="lg" className="w-full text-base">
                  <a href={dmUrl} target="_blank" rel="noopener noreferrer">
                    Open Telegram
                  </a>
                </Button>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-sm uppercase"><span className="bg-background px-2 text-muted-foreground">or</span></div>
            </div>

            <div className="space-y-3">
              <p className="text-base font-medium">Group or Channel</p>
              <p className="text-sm text-muted-foreground">
                Add <strong>@UptrackAppBot</strong> to your group or channel, then send:
              </p>
              {code && (
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md bg-muted px-4 py-3 text-center text-lg font-mono font-semibold tracking-widest">
                    /connect {code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`/connect ${code}`)
                      toast.success('Copied to clipboard')
                    }}
                  >
                    Copy
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <div className="flex w-full items-center gap-2 text-sm text-muted-foreground">
              <div className="h-2 w-2 animate-pulse rounded-full bg-status-degraded" />
              Waiting for connection...
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ── Delivery History Tab ────────────────────────────────────────────

function DeliveryHistoryTab() {
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data: statsData } = useQuery({
    queryKey: ['notification-delivery-stats'],
    queryFn: () => apiV2.getNotificationDeliveryStats(7),
  })

  const { data: deliveriesData, isLoading } = useQuery({
    queryKey: ['notification-deliveries', statusFilter],
    queryFn: () =>
      apiV2.getNotificationDeliveries({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
      }),
  })

  const deliveries = deliveriesData?.data ?? []
  const stats = statsData?.stats ?? {}
  const delivered = stats['delivered'] ?? 0
  const failed = stats['failed'] ?? 0
  const skipped = stats['skipped'] ?? 0
  const pending = stats['pending'] ?? 0
  const total = delivered + failed + skipped + pending

  return (
    <div className="space-y-4">
      {/* Stats summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-semibold">{total}</div>
            <p className="text-sm text-muted-foreground">Total (7d)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-semibold text-status-up">{delivered}</div>
            <p className="text-sm text-muted-foreground">Delivered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-semibold text-status-down">{failed}</div>
            <p className="text-sm text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-semibold text-status-degraded">{skipped}</div>
            <p className="text-sm text-muted-foreground">Skipped</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Label htmlFor="delivery-status-filter" className="text-sm text-muted-foreground">Filter by status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger id="delivery-status-filter" className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Delivery table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-4 p-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : deliveries.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery.id}>
                    <TableCell>
                      <DeliveryStatusBadge status={delivery.status} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{formatChannelType(delivery.channel_type)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatEventType(delivery.event_type)}
                    </TableCell>
                    <TableCell className="max-w-[300px] text-sm text-muted-foreground">
                      {delivery.error_message ? (
                        <span className="text-status-down">{delivery.error_message}</span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatRelativeTime(delivery.inserted_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-12 text-center">
              <CatSleeping size={80} className="mx-auto mb-3" />
              <p className="text-muted-foreground">
                No notification deliveries yet. Deliveries will appear here when alerts are triggered.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────

function DeliveryStatusBadge({ status }: { status: V2NotificationDelivery['status'] }) {
  switch (status) {
    case 'delivered':
      return <StatusBadge kind="up" label="Delivered" />
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    case 'skipped':
      return <Badge variant="secondary">Skipped</Badge>
    case 'pending':
      return <Badge variant="outline">Pending</Badge>
  }
}

function formatChannelType(type: string): string {
  const labels: Record<string, string> = {
    email: 'Email',
    slack: 'Slack',
    discord: 'Discord',
    telegram: 'Telegram',
    webhook: 'Webhook',
    sms: 'SMS',
    voice: 'Phone call',
  }
  return labels[type] ?? type
}

function formatEventType(type: string): string {
  const labels: Record<string, string> = {
    incident_created: 'Incident Created',
    incident_resolved: 'Incident Resolved',
    test: 'Test Alert',
  }
  return labels[type] ?? type
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString()
}
